<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading || isSubmitting" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="rm-badge-section">{{ isEditMode ? 'تعديل مجموعة' : 'إضافة مجموعة جديدة' }}</span>
          <div class="rm-breadcrumb">
            <router-link to="/trip-groups" class="rm-bc-link">مجموعات الرحلات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="rm-bc-item rm-bc-active">{{ isEditMode ? `تعديل ${form.group_code || ''}` : 'مجموعة جديدة' }}</span>
          </div>
        </div>
        <h1 class="rm-main-title">{{ isEditMode ? `تعديل مجموعة الرحلات: ${form.name || form.group_code}` : 'إضافة مجموعة رحلات جديدة' }}</h1>
        <p class="rm-subtitle">تجميع الرحلات التشغيلية للمسارات وضبط معايير المجموعة ومراجعة إجمالي المسافات</p>
      </div>

      <div class="rm-header-actions">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="goBack"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          إلغاء والعودة
        </v-btn>
      </div>
    </div>

    <!-- ==================== 2-COLUMN LAYOUT ==================== -->
    <div class="rm-create-grid">
      <!-- ==================== COLUMN 1: FORM & SELECTION (65%) ==================== -->
      <div class="rm-form-col">
        <!-- Section 1: Group Basic Data -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center mb-4">
            <div class="rm-icon-header-box me-3">
              <v-icon size="20" color="#082C69">mdi-card-text-outline</v-icon>
            </div>
            <div>
              <h2 class="rm-card-title">1. بيانات المجموعة</h2>
              <p class="rm-card-subtitle">حدد اسم المجموعة وحالتها التشغيلية</p>
            </div>
          </div>

          <v-row dense>
            <!-- Group Code (Read-only) -->
            <v-col cols="12" md="3">
              <label class="rm-label">كود المجموعة</label>
              <v-text-field
                :value="form.group_code || 'يتم إنشاؤه تلقائياً'"
                outlined
                dense
                disabled
                class="rm-custom-input disabled-code-field"
                prepend-inner-icon="mdi-identifier"
              ></v-text-field>
            </v-col>

            <!-- Group Name (Required) -->
            <v-col cols="12" md="4">
              <label class="rm-label">اسم المجموعة *</label>
              <v-text-field
                v-model="form.name"
                outlined
                dense
                placeholder="مثال: مجموعة الرحلات الصباحية - القاهرة"
                :rules="[v => !!v || 'اسم المجموعة مطلوب']"
                class="rm-custom-input"
                prepend-inner-icon="mdi-format-title"
              ></v-text-field>
            </v-col>

            <!-- Initial Status -->
            <v-col cols="12" md="2">
              <label class="rm-label">الحالة</label>
              <v-select
                v-model="form.status"
                :items="statusOptions"
                item-text="label"
                item-value="value"
                outlined
                dense
                class="rm-custom-input"
              ></v-select>
            </v-col>

            <!-- Operation User (Role 4) -->
            <v-col cols="12" md="3">
              <label class="rm-label">مسؤول التشغيل</label>
              <v-autocomplete
                v-model="form.operation_user_id"
                :items="operationUsersList"
                item-text="displayLabel"
                item-value="id"
                placeholder="اختياري — غير معيّن"
                outlined
                dense
                clearable
                no-data-text="لا يوجد موظفو تشغيل نشطون"
                class="rm-custom-input"
                prepend-inner-icon="mdi-account-tie-outline"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </div>

        <!-- Section 2: Planned Trip Selection -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-checkbox-multiple-marked-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">2. اختيار الرحلات الأساسية المتاحة</h2>
                <p class="rm-card-subtitle">اختر الرحلات الأساسية (Parent Trips) غير المرتبطة بمجموعات أخرى لإضافتها</p>
              </div>
            </div>

            <!-- Quick Selection Counter -->
            <div class="caption text-muted">
              تم تحديد <strong class="text-navy">{{ selectedTrips.length }}</strong> رحلة أساسية
            </div>
          </div>

          <!-- Trip Search & Filter Bar -->
          <div class="rm-trip-filter-box mb-4 pa-3 rounded-lg">
            <v-row dense class="align-center">
              <!-- Search -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="tripFilters.search"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="ابحث بكود الرحلة (T-XXXX) أو المسار..."
                  outlined
                  dense
                  hide-details
                  clearable
                  class="rm-filter-input"
                  @input="onTripFilterChange"
                ></v-text-field>
              </v-col>

              <!-- Route Filter -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="tripFilters.route_id"
                  :items="routesList"
                  item-text="displayName"
                  item-value="id"
                  placeholder="كل المسارات"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="rm-filter-input"
                  @change="onTripFilterChange"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </div>

          <!-- Available Trips Table -->
          <div class="table-responsive">
            <table class="rm-custom-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center">
                    <input
                      type="checkbox"
                      :checked="isAllVisibleSelected"
                      @change="toggleSelectAllVisible"
                      class="rm-checkbox"
                    />
                  </th>
                  <th>كود الرحلة</th>
                  <th>المسار</th>
                  <th>وقت الانطلاق</th>
                  <th>وقت الوصول</th>
                  <th>أيام التشغيل</th>
                  <th class="text-center">التشغيلات القادمة</th>
                  <th class="text-center">المسافة</th>
                  <th class="text-center">الحالة</th>
                </tr>
              </thead>
              <tbody v-if="availableTrips.length > 0">
                <tr
                  v-for="trip in availableTrips"
                  :key="trip.id"
                  :class="{ 'row-selected': isTripSelected(trip.id) }"
                  @click="toggleTripSelection(trip)"
                  style="cursor: pointer;"
                >
                  <td class="text-center" @click.stop>
                    <input
                      type="checkbox"
                      :checked="isTripSelected(trip.id)"
                      @change="toggleTripSelection(trip)"
                      class="rm-checkbox"
                    />
                  </td>
                  <td>
                    <span class="rm-code-pill font-weight-bold">{{ trip.trip_code }}</span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="font-weight-medium text-navy">{{ trip.route_name }}</span>
                      <span class="caption text-muted">{{ trip.route_code }}</span>
                    </div>
                  </td>
                  <td class="caption font-weight-medium">
                    {{ trip.first_stop_time ? trip.first_stop_time.slice(0, 5) : '—' }}
                  </td>
                  <td class="caption">
                    {{ trip.last_stop_time ? trip.last_stop_time.slice(0, 5) : '—' }}
                  </td>
                  <td class="caption">
                    <span class="rm-days-badge">{{ formatOperatingDays(trip.operating_days) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="rm-badge-count">{{ trip.upcoming_occurrences_count || 0 }}</span>
                  </td>
                  <td class="text-center caption font-weight-bold">
                    {{ trip.distance_km }} كم
                  </td>
                  <td class="text-center">
                    <span :class="['rm-trip-status-pill', trip.status_id === 1 ? 'status-scheduled' : 'status-cancelled']">
                      {{ trip.status_label }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="9" class="text-center py-8 text-muted caption">
                    لا توجد رحلات أساسية متاحة وفق معايير البحث
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Trips Pagination -->
          <div v-if="tripPagination.total_pages > 1" class="d-flex align-center justify-space-between mt-3 pt-3 border-top">
            <span class="caption text-muted">
              إجمالي المتاح: {{ tripPagination.total }} رحلة
            </span>
            <v-pagination
              v-model="tripPagination.current_page"
              :length="tripPagination.total_pages"
              :total-visible="5"
              color="#082C69"
              @input="fetchAvailableTrips"
            ></v-pagination>
          </div>
        </div>
      </div>

      <!-- ==================== COLUMN 2: SELECTED TRIPS & SUMMARY (35%) ==================== -->
      <div class="rm-summary-col">
        <!-- Selected Trips List Card -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-format-list-checks</v-icon>
              </div>
              <h2 class="rm-card-title">الرحلات المختارة</h2>
            </div>
            <span class="rm-badge-count">{{ selectedTrips.length }} رحلات</span>
          </div>

          <!-- Selected Items Scroll Area -->
          <div v-if="selectedTrips.length > 0" class="rm-selected-trips-scroll">
            <div
              v-for="trip in selectedTrips"
              :key="trip.id"
              class="rm-selected-trip-item d-flex align-center justify-space-between p-2 mb-2 rounded"
            >
              <div>
                <div class="d-flex align-center gap-1 mb-1">
                  <span class="caption font-weight-bold text-navy">{{ trip.trip_code || `T-${String(trip.id).padStart(4, '0')}` }}</span>
                  <span class="caption text-muted">• {{ formatOperatingDays(trip.operating_days) }}</span>
                </div>
                <div class="caption text-truncate text-navy" style="max-width: 200px;">
                  {{ trip.route_name }} ({{ trip.route_code }})
                </div>
                <div class="caption text-muted mt-1">
                  <v-icon x-small color="#64748B">mdi-clock-outline</v-icon>
                  {{ trip.first_stop_time ? trip.first_stop_time.slice(0, 5) : '—' }} • {{ trip.distance_km || 0 }} كم
                </div>
              </div>

              <!-- Remove Action -->
              <v-btn
                icon
                x-small
                color="#EF4444"
                @click="removeTripFromSelection(trip.id)"
                class="ms-2"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else class="text-center py-8 text-muted caption">
            <v-icon size="32" color="#CBD5E1" class="d-block mb-2">mdi-checkbox-blank-circle-outline</v-icon>
            لم يتم اختيار أي رحلات بعد.<br>حدد الرحلات من الجدول على اليمين.
          </div>
        </div>

        <!-- Summary Statistics Card -->
        <div class="rm-card mb-6">
          <h3 class="rm-card-title mb-4">ملخص المجموعة</h3>

          <div class="rm-summary-metrics-grid mb-4">
            <div class="rm-summary-mini-card">
              <span class="mini-label">إجمالي الرحلات</span>
              <span class="mini-value text-navy">{{ selectedTrips.length }}</span>
            </div>

            <div class="rm-summary-mini-card">
              <span class="mini-label">إجمالي المسافة</span>
              <span class="mini-value text-navy">{{ calculatedTotalDistance }} كم</span>
            </div>

            <div class="rm-summary-mini-card">
              <span class="mini-label">عدد المسارات</span>
              <span class="mini-value text-navy">{{ uniqueRoutesCount }}</span>
            </div>
          </div>

          <div class="rm-info-note pa-3 rounded d-flex align-start gap-2">
            <v-icon small color="#0284C7" class="mt-1">mdi-information</v-icon>
            <span class="caption text-muted">
              يمكن حفظ المجموعة كمسودة بدون رحلات، ولكن نشر المجموعة يتطلب وجود رحلة واحدة على الأقل.
            </span>
          </div>
        </div>

        <!-- Actions Card -->
        <div class="rm-card">
          <div class="d-flex flex-column gap-2">
            <v-btn
              depressed
              class="rm-btn-primary w-100 mb-2"
              @click="submitGroup('published')"
              :disabled="selectedTrips.length === 0"
            >
              <v-icon right size="18" class="ms-1">mdi-send</v-icon>
              نشر المجموعة
            </v-btn>

            <v-btn
              depressed
              class="rm-btn-secondary w-100"
              @click="submitGroup('draft')"
            >
              <v-icon right size="18" class="ms-1">mdi-content-save-outline</v-icon>
              حفظ كمسودة
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "RouteMakingCreateEditTripGroup",
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      isEditMode: false,
      groupId: null,
      form: {
        group_code: "",
        name: "",
        status: "draft",
        operation_user_id: null,
      },
      statusOptions: [
        { label: "مسودة", value: "draft" },
        { label: "منشورة", value: "published" },
        { label: "غير نشطة", value: "inactive" },
      ],
      routesList: [],
      operationUsersList: [],
      currentOperationUser: null,
      availableTrips: [],
      selectedTrips: [],
      tripFilters: {
        search: "",
        route_id: null,
        start_date: null,
      },
      tripPagination: {
        current_page: 1,
        total_pages: 1,
        per_page: 15,
        total: 0,
      },
      debounceTimeout: null,
    };
  },
  computed: {
    calculatedTotalDistance() {
      const sum = this.selectedTrips.reduce((acc, t) => acc + (Number(t.distance_km) || 0), 0);
      return Math.round(sum * 10) / 10;
    },
    uniqueRoutesCount() {
      const set = new Set();
      this.selectedTrips.forEach((t) => {
        if (t.route_id) set.add(t.route_id);
      });
      return set.size;
    },
    isAllVisibleSelected() {
      if (this.availableTrips.length === 0) return false;
      return this.availableTrips.every((t) => this.isTripSelected(t.id));
    },
  },
  created() {
    this.groupId = this.$route.params.id || null;
    this.isEditMode = !!this.groupId;

    this.fetchRoutes();
    this.fetchOperationUsers();
    if (this.isEditMode) {
      this.fetchGroupDetails();
    } else {
      this.fetchAvailableTrips();
    }
  },
  methods: {
    toOperationUserItem(u) {
      return {
        id: u.id,
        name: u.name,
        email: u.email,
        displayLabel: u.email ? `${u.name} (${u.email})` : u.name,
      };
    },
    fetchOperationUsers() {
      axios
        .get("/trip-groups/eligible-operation-users")
        .then((res) => {
          // API contract: { success, data: { available, message, users: [...] } }
          const payload = res && res.data ? res.data.data : null;
          const list = Array.isArray(payload)
            ? payload
            : (payload && Array.isArray(payload.users) ? payload.users : []);
          const mapped = list.map((u) => this.toOperationUserItem(u));
          // Keep the currently assigned user selectable even if loaded first by group details
          if (this.currentOperationUser && !mapped.some((u) => u.id === this.currentOperationUser.id)) {
            mapped.push(this.currentOperationUser);
          }
          this.operationUsersList = mapped;
        })
        .catch((err) => {
          console.error("Error loading eligible operation users:", err);
        });
    },
    fetchRoutes() {
      axios
        .get("/routes/all")
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          this.routesList = list.map((r) => ({
            id: r.id,
            name: r.name,
            displayName: `${r.route_code || 'R-' + String(r.id).padStart(4, '0')} - ${r.name}`,
          }));
        })
        .catch((err) => {
          console.error("Error loading routes list:", err);
        });
    },
    fetchGroupDetails() {
      this.isLoading = true;
      axios
        .get(`/trip-groups/${this.groupId}`)
        .then((res) => {
          if (res.data && res.data.success) {
            const data = res.data.data;
            this.form.group_code = data.group.group_code;
            this.form.name = data.group.name;
            this.form.status = data.group.status;
            const cou = data.current_operation_user;
            this.form.operation_user_id = cou ? cou.id : null;
            if (cou) {
              this.currentOperationUser = this.toOperationUserItem(cou);
              if (!this.operationUsersList.some((u) => u.id === cou.id)) {
                this.operationUsersList = [...this.operationUsersList, this.currentOperationUser];
              }
            }

            // Load existing parent trips into selected list
            const existingTrips = data.trips || data.planned_trips || [];
            this.selectedTrips = existingTrips.map((t) => ({
              id: t.id,
              trip_code: t.trip_code || t.trip_reference || `T-${String(t.id).padStart(4, '0')}`,
              route_id: t.route_id,
              route_name: t.route_name,
              route_code: t.route_code,
              first_stop_time: t.first_stop_time,
              last_stop_time: t.last_stop_time,
              operating_days: t.operating_days,
              formatted_operating_days: t.formatted_operating_days,
              upcoming_occurrences_count: t.upcoming_occurrences_count || 0,
              distance_km: t.distance_km,
            }));

            // Fetch available trips after loading existing group trips
            this.fetchAvailableTrips();
          }
        })
        .catch((err) => {
          console.error("Error loading group details:", err);
          this.notifyUser("error", "خطأ", "تعذر تحميل بيانات المجموعة");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    fetchAvailableTrips() {
      const params = {
        page: this.tripPagination.current_page,
        per_page: this.tripPagination.per_page,
        search: this.tripFilters.search || undefined,
        route_id: this.tripFilters.route_id || undefined,
        group_id: this.groupId || undefined,
      };

      axios
        .get("/trip-groups/available-trips", { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const data = res.data.data;
            this.availableTrips = data.data || [];
            this.tripPagination.current_page = data.current_page || 1;
            this.tripPagination.total_pages = data.last_page || 1;
            this.tripPagination.total = data.total || 0;
          }
        })
        .catch((err) => {
          console.error("Error fetching available trips:", err);
        });
    },
    onTripFilterChange() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.tripPagination.current_page = 1;
        this.fetchAvailableTrips();
      }, 300);
    },
    isTripSelected(tripId) {
      return this.selectedTrips.some((t) => t.id === tripId);
    },
    toggleTripSelection(trip) {
      const idx = this.selectedTrips.findIndex((t) => t.id === trip.id);
      if (idx > -1) {
        this.selectedTrips.splice(idx, 1);
      } else {
        this.selectedTrips.push({
          id: trip.id,
          trip_code: trip.trip_code || trip.trip_reference || `T-${String(trip.id).padStart(4, '0')}`,
          route_id: trip.route_id,
          route_name: trip.route_name,
          route_code: trip.route_code,
          first_stop_time: trip.first_stop_time,
          last_stop_time: trip.last_stop_time,
          operating_days: trip.operating_days,
          formatted_operating_days: trip.formatted_operating_days,
          upcoming_occurrences_count: trip.upcoming_occurrences_count || 0,
          distance_km: trip.distance_km,
        });
      }
    },
    toggleSelectAllVisible() {
      if (this.isAllVisibleSelected) {
        // Deselect visible
        const visibleIds = new Set(this.availableTrips.map((t) => t.id));
        this.selectedTrips = this.selectedTrips.filter((t) => !visibleIds.has(t.id));
      } else {
        // Select all visible
        this.availableTrips.forEach((trip) => {
          if (!this.isTripSelected(trip.id)) {
            this.selectedTrips.push({
              id: trip.id,
              trip_code: trip.trip_code || trip.trip_reference || `T-${String(trip.id).padStart(4, '0')}`,
              route_id: trip.route_id,
              route_name: trip.route_name,
              route_code: trip.route_code,
              first_stop_time: trip.first_stop_time,
              last_stop_time: trip.last_stop_time,
              operating_days: trip.operating_days,
              formatted_operating_days: trip.formatted_operating_days,
              upcoming_occurrences_count: trip.upcoming_occurrences_count || 0,
              distance_km: trip.distance_km,
            });
          }
        });
      }
    },
    removeTripFromSelection(tripId) {
      this.selectedTrips = this.selectedTrips.filter((t) => t.id !== tripId);
    },
    formatOperatingDays(days) {
      if (!days) return "—";
      let arr = days;
      if (typeof days === "string") {
        try {
          arr = JSON.parse(days);
        } catch (e) {
          return days;
        }
      }
      if (!Array.isArray(arr) || arr.length === 0) return "—";

      const sunThu = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
      if (arr.length === 5 && sunThu.every((d) => arr.includes(d))) {
        return "الأحد – الخميس";
      }
      if (arr.length === 7) {
        return "يومياً";
      }
      return arr.join("، ");
    },
    getTripStatusClass(trip) {
      const code = trip.status_code || trip.status_label;
      if (code === "cancelled" || code === "ملغاة") return "status-cancelled";
      if (code === "completed" || code === "مكتملة") return "status-completed";
      if (code === "in_progress" || code === "جاري التشغيل") return "status-in-progress";
      return "status-scheduled";
    },
    submitGroup(targetStatus) {
      if (!this.form.name || !this.form.name.trim()) {
        this.notifyUser("error", "بيانات ناقصة", "يرجى إدخال اسم المجموعة أولاً");
        return;
      }

      if (targetStatus === "published" && this.selectedTrips.length === 0) {
        this.notifyUser("warn", "تنبيه", "لا يمكن نشر المجموعة بدون إضافة رحلة أساسية واحدة على الأقل");
        return;
      }

      this.isSubmitting = true;
      const payload = {
        name: this.form.name.trim(),
        status: targetStatus,
        trip_ids: this.selectedTrips.map((t) => t.id),
        operation_user_id: this.form.operation_user_id || null,
      };

      const request = this.isEditMode
        ? axios.put(`/trip-groups/${this.groupId}`, payload)
        : axios.post("/trip-groups", payload);

      request
        .then((res) => {
          if (res.data && res.data.success) {
            this.notifyUser("success", "تم بنجاح", res.data.message || "تم حفظ مجموعة الرحلات");
            const targetId = res.data.data?.id || this.groupId;
            this.$router.push(`/trip-groups/${targetId}`);
          } else {
            this.notifyUser("error", "فشل الحفظ", res.data?.message || "حدث خطأ أثناء الحفظ");
          }
        })
        .catch((err) => {
          console.error("Error saving trip group:", err);
          const errData = err.response?.data;
          let msg = "حدث خطأ أثناء حفظ المجموعة";
          if (errData?.errors) {
            const first = Object.values(errData.errors)[0];
            msg = Array.isArray(first) ? first[0] : String(first);
          } else if (errData?.message) {
            msg = errData.message;
          }
          this.notifyUser("error", "خطأ في الطلب", msg);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    goBack() {
      this.$router.push("/trip-groups");
    },
    notifyUser(type, title, text) {
      if (this.$swal) {
        this.$swal({
          icon: type,
          title: title,
          text: text,
          timer: 3500,
          showConfirmButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-block;
  background-color: rgba(8, 44, 105, 0.08);
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 6px;
}

.rm-breadcrumb {
  font-size: 12px;
  color: #64748B;
}

.rm-bc-link {
  color: #082C69;
  text-decoration: none;
  font-weight: 600;
}
.rm-bc-link:hover {
  text-decoration: underline;
}

.rm-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 4px 0 0 0;
}

/* Grid */
.rm-create-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 1024px) {
  .rm-create-grid {
    grid-template-columns: 1fr;
  }
}

/* Cards */
.rm-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 12px;
  color: #64748B;
  margin: 2px 0 0 0;
}

.rm-icon-header-box {
  width: 36px;
  height: 36px;
  background: #EEF2F6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.rm-trip-filter-box {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

/* Table */
.table-responsive {
  overflow-x: auto;
}

.rm-custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rm-custom-table th {
  background-color: #F8FAFC;
  color: #475569;
  font-weight: 700;
  padding: 10px 12px;
  text-align: right;
  border-bottom: 2px solid #E2E8F0;
  white-space: nowrap;
}

.rm-custom-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.row-selected {
  background-color: #F0F9FF !important;
}

.rm-trip-status-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.status-scheduled {
  background-color: #ECFDF5 !important;
  color: #065F46 !important;
  border: 1px solid #A7F3D0 !important;
}

.status-in-progress {
  background-color: #EFF6FF !important;
  color: #1E40AF !important;
  border: 1px solid #BFDBFE !important;
}

.status-completed {
  background-color: #F1F5F9 !important;
  color: #475569 !important;
  border: 1px solid #CBD5E1 !important;
}

.status-cancelled {
  background-color: #FEF2F2 !important;
  color: #991B1B !important;
  border: 1px solid #FECACA !important;
}

/* Selected Trips Scroll */
.rm-selected-trips-scroll {
  max-height: 280px;
  overflow-y: auto;
}

.rm-selected-trip-item {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  padding: 8px 12px;
}

.rm-badge-count {
  background-color: #082C69;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Summary Mini Metrics */
.rm-summary-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.rm-summary-mini-card {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 6px;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.mini-label {
  font-size: 11px;
  color: #64748B;
  margin-bottom: 2px;
}

.mini-value {
  font-size: 18px;
  font-weight: 800;
}

.rm-info-note {
  background-color: #F0F9FF;
  border: 1px solid #BAE6FD;
}

/* Buttons */
.rm-btn-primary {
  background-color: #082C69 !important;
  color: #FFFFFF !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-radius: 10px !important;
  text-transform: none !important;
  padding: 0 16px !important;
  height: 42px !important;
}

.rm-btn-secondary {
  background-color: #FFFFFF !important;
  color: #082C69 !important;
  border: 1px solid #CBD5E1 !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-radius: 10px !important;
  text-transform: none !important;
  padding: 0 14px !important;
  height: 42px !important;
}

.text-navy { color: #082C69 !important; }
.border-top { border-top: 1px solid #E2E8F0; }
</style>
